// Own-head prediction (lane C, backlog C-2).
//
// The display time of the own head is "now + RTT" in the arrival-time domain: an ARENA_INPUT
// sent at time t reaches the server about RTT/2 later, and the frame showing its effect arrives
// another RTT/2 after that, so a frame for tick T that arrived at `a_T` shows the state the
// server will have reached by the time an input sent at `a_T − RTT` takes effect. Predicting to
// `now + RTT` therefore puts the head where the server will be when the input just sent applies,
// so a turn starts on the very next rendered frame.
//
// Each sample: start from the newest own head record (tick T, arrival a_T, ARENA_OWN.inputSeq S);
// step it with the SHARED movement function (`stepHead`, @ballio/shared arena/movement.ts) for
// (now + RTT − a_T) / tickMs ticks (the last one fractional), where the target in effect at
// simulated time τ is the newest input whose seq is S, or after S and sent at ≤ τ − RTT. Inputs
// are quantised with `headingToWire` before they are sent and recorded, so the predictor steers
// toward exactly the angle the server will. Corrections (a new server head, an RTT change) blend
// out over ARENA_CLIENT.correctionBlendMs; a jump above SNAP_CELLS snaps (spawn, teleport).
// Acceptance: RMS error vs the server ≤ 0.1 cell at 100 ms RTT ± 20 ms; no snap > 0.25 cell.

import {
  ARENA_CLIENT,
  type ArenaGridShape,
  type ArenaHeadMotion,
  type ArenaMoveParams,
  headingFromWire,
  stepHead,
  type WelcomeArenaInfo,
} from '@ballio/shared';

/** The movement step (the shared one; tests may inject their own). */
export type HeadStepper = (
  head: ArenaHeadMotion,
  targetHeading: number,
  p: ArenaMoveParams,
  grid: ArenaGridShape,
) => ArenaHeadMotion;

/** Never predict further ahead than this (a bad RTT estimate, a stalled feed). */
export const MAX_HORIZON_MS = 400;
/** A correction this large snaps instead of blending. */
export const SNAP_CELLS = 3;
/** Inputs kept for replay. */
const MAX_INPUTS = 256;

const TAU = Math.PI * 2;

/** Seq order with u16 wrap: true if `a` is after `b`. */
export function seqAfter(a: number, b: number): boolean {
  const d = (a - b) & 0xffff;
  return d !== 0 && d < 0x8000;
}

/**
 * Turns `heading` toward `target` by at most `maxStep` (shorter way; an exact reverse turns +).
 * The fallback when the shared step is unavailable; matches the shared contract.
 */
export function turnTowardLocal(heading: number, target: number, maxStep: number): number {
  let d = (target - heading) % TAU;
  if (d < 0) d += TAU;
  if (d > Math.PI) d -= TAU; // (−π, π]: an exact reverse is +π
  const step = Math.abs(d) <= maxStep ? d : Math.sign(d) * maxStep;
  const r = (heading + step) % TAU;
  return r < 0 ? r + TAU : r;
}

/**
 * A plain stepper (turn limit, constant speed, circle or square wall slide): used only if the
 * shared one throws, so the game stays playable.
 */
export function fallbackStep(
  head: ArenaHeadMotion,
  targetHeading: number,
  p: ArenaMoveParams,
  grid: ArenaGridShape,
): ArenaHeadMotion {
  head.heading = turnTowardLocal(head.heading, targetHeading, p.turnRateRad * p.dt);
  let nx = head.x + Math.cos(head.heading) * p.speed * p.dt;
  let ny = head.y + Math.sin(head.heading) * p.speed * p.dt;
  if (grid.shape === 'circle') {
    const cx = grid.cols / 2;
    const cy = grid.rows / 2;
    const r = Math.min(grid.cols, grid.rows) / 2 - 0.51;
    const dx = nx - cx;
    const dy = ny - cy;
    const d = Math.hypot(dx, dy);
    if (d > r) {
      nx = cx + (dx / d) * r;
      ny = cy + (dy / d) * r;
    }
  } else {
    nx = Math.min(grid.cols - 0.001, Math.max(0, nx));
    ny = Math.min(grid.rows - 0.001, Math.max(0, ny));
  }
  head.x = nx;
  head.y = ny;
  return head;
}

interface SentInput {
  readonly seq: number;
  /** Radians (from the wire steps). */
  readonly heading: number;
  readonly atMs: number;
}

export interface PredictedHead {
  x: number;
  y: number;
  heading: number;
}

export interface PredictorOptions {
  readonly step?: HeadStepper;
}

export class OwnHeadPredictor {
  private readonly grid: ArenaGridShape;
  private readonly params: { speed: number; turnRateRad: number; dt: number };
  private readonly tickMs: number;
  private step: HeadStepper;
  private readonly inputs: SentInput[] = [];
  private base: { x: number; y: number; heading: number; atMs: number; seq: number } | null = null;
  /** Correction offset blended out over correctionBlendMs. */
  private errX = 0;
  private errY = 0;
  /** When the offset was last decayed. */
  private errAtMs = Number.NaN;
  /** The RTT the last sample used (reconcile evaluates both predictions with it). */
  private lastRttMs = 0;
  private readonly scratch: ArenaHeadMotion = { x: 0, y: 0, heading: 0 };
  /** Largest correction blended so far (diagnostics; the C-2 "no snap" check). */
  maxCorrection = 0;

  constructor(info: WelcomeArenaInfo, tickRate: number, opts: PredictorOptions = {}) {
    this.grid = { shape: info.shape, cols: info.cols, rows: info.rows };
    const rate = tickRate > 0 ? tickRate : 25;
    this.tickMs = 1000 / rate;
    this.params = {
      speed: info.speed,
      turnRateRad: (info.turnRateDeg * Math.PI) / 180,
      dt: 1 / rate,
    };
    this.step = opts.step ?? stepHead;
  }

  /** Forget the life (death, respawn, room change). */
  reset(): void {
    this.inputs.length = 0;
    this.base = null;
    this.errX = 0;
    this.errY = 0;
    this.errAtMs = Number.NaN;
  }

  get hasBase(): boolean {
    return this.base !== null;
  }

  /** A sent ARENA_INPUT (seq, heading steps) at its send time. */
  noteInput(seq: number, heading: number, atMs: number): void {
    this.inputs.push({ seq, heading: headingFromWire(heading), atMs });
    if (this.inputs.length > MAX_INPUTS) this.inputs.shift();
  }

  /**
   * The server's own-head record, its applied seq (ARENA_OWN.inputSeq) and the arrival time of
   * the frame that carried it. The jump between the old and the new prediction (both evaluated
   * at the arrival time) becomes an offset that blends out.
   */
  reconcile(
    head: { x: number; y: number; heading: number },
    appliedSeq: number,
    arrivedAtMs: number,
  ): void {
    const had = this.base !== null;
    const before = had ? this.predict(arrivedAtMs, this.lastRttMs) : null;
    this.base = {
      x: head.x,
      y: head.y,
      heading: headingFromWire(head.heading),
      atMs: arrivedAtMs,
      seq: appliedSeq,
    };
    // Drop the inputs the server has applied, except the newest of them (still in effect).
    let keepFrom = this.inputs.length;
    for (let i = 0; i < this.inputs.length; i++) {
      const s = (this.inputs[i] as SentInput).seq;
      if (s === appliedSeq || seqAfter(s, appliedSeq)) {
        keepFrom = i;
        break;
      }
    }
    if (keepFrom === this.inputs.length && keepFrom > 0) keepFrom = this.inputs.length - 1;
    if (keepFrom > 0) this.inputs.splice(0, keepFrom);
    if (!before) return;
    this.decay(arrivedAtMs);
    const after = this.predict(arrivedAtMs, this.lastRttMs);
    this.errX += before.x - after.x;
    this.errY += before.y - after.y;
    const e = Math.hypot(this.errX, this.errY);
    if (e > SNAP_CELLS) {
      this.errX = 0;
      this.errY = 0;
    } else if (e > this.maxCorrection) {
      this.maxCorrection = e;
    }
  }

  /** The predicted head at render time `nowMs`, or null before the first server head. */
  sample(nowMs: number, rttMs: number): PredictedHead | null {
    if (!this.base) return null;
    this.lastRttMs = Math.min(Math.max(0, rttMs), MAX_HORIZON_MS);
    const raw = this.predict(nowMs, this.lastRttMs);
    this.decay(nowMs);
    return { x: raw.x + this.errX, y: raw.y + this.errY, heading: raw.heading };
  }

  /** Blends the correction offset out (≈ 95 % gone after correctionBlendMs). */
  private decay(nowMs: number): void {
    if (Number.isFinite(this.errAtMs) && nowMs > this.errAtMs) {
      const k = Math.exp(-(nowMs - this.errAtMs) / (ARENA_CLIENT.correctionBlendMs / 3));
      this.errX *= k;
      this.errY *= k;
    }
    if (!Number.isFinite(this.errAtMs) || nowMs > this.errAtMs) this.errAtMs = nowMs;
  }

  /** The un-blended prediction (tests and the bench use it directly). */
  predict(nowMs: number, rttMs: number): PredictedHead {
    const b = this.base;
    if (!b) return { x: 0, y: 0, heading: 0 };
    const h = this.scratch;
    h.x = b.x;
    h.y = b.y;
    h.heading = b.heading;
    const horizonMs = Math.min(Math.max(0, nowMs + rttMs - b.atMs), MAX_HORIZON_MS + rttMs);
    let ticks = horizonMs / this.tickMs;
    let tau = b.atMs;
    // The target in effect: the applied input (if still known), else the head's own heading.
    let target = b.heading;
    let next = 0;
    for (; next < this.inputs.length; next++) {
      const inp = this.inputs[next] as SentInput;
      if (inp.seq === b.seq) target = inp.heading;
      if (seqAfter(inp.seq, b.seq)) break;
    }
    while (ticks > 1e-9) {
      const part = ticks >= 1 ? 1 : ticks;
      tau += this.tickMs * part;
      while (next < this.inputs.length) {
        const inp = this.inputs[next] as SentInput;
        if (inp.atMs + rttMs > tau) break;
        target = inp.heading;
        next++;
      }
      this.stepOnce(h, target, part);
      ticks -= part;
    }
    return { x: h.x, y: h.y, heading: h.heading };
  }

  private stepOnce(h: ArenaHeadMotion, target: number, part: number): void {
    const p =
      part === 1
        ? this.params
        : {
            speed: this.params.speed,
            turnRateRad: this.params.turnRateRad,
            dt: this.params.dt * part,
          };
    try {
      this.step(h, target, p, this.grid);
    } catch {
      // The shared step is not available (yet): keep playing on the local one.
      this.step = fallbackStep;
      fallbackStep(h, target, p, this.grid);
    }
  }
}
